import React from 'react';
import { Link } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DetailsCard from '../DetailsCard';
import UsageMeter from './UsageMeter';
import useSubscriptionFeatures from '../../../../../hooks/useSubscriptionFeatures';
import { SUBSCRIPTION_LIMITS } from '../../../../../consts/SubscriptionFeatures';
import { LIMIT_UPGRADE_BUTTON } from '../../../../../components/subscription/planLockTexts';
import styles from './PlanUsageCard.module.css';

// Linhas de features do plano no banco (system_subscription_plans.features).
// Asterisco no fim = integração de terceiro, contratada à parte.
const featureLines = (plan, skipIntro) => (plan?.features || [])
  .map((f) => (typeof f === 'object' ? (f.name || f.description || '') : String(f || '')).trim())
  .filter((text) => text && !(skipIntro && /^tudo do/i.test(text)));

// Próximo plano principal acima do atual, na ordem da tela de planos
const findNextPlan = (plans, current) => Object.values(plans || {})
  .filter((p) => p.type === 'main' && Number(p.displayOrder) > Number(current.displayOrder))
  .sort((x, y) => Number(x.displayOrder) - Number(y.displayOrder))[0] || null;

/** Uso do plano (agendas, usuários, salas, procedimentos) e recursos liberados. */
const PlanUsageCard = ({ usage, hasPlan, plans, currentPlanId }) => {
  const { getLimit } = useSubscriptionFeatures();
  // eslint-disable-next-line eqeqeq
  const currentPlan = Object.values(plans || {}).find((p) => p.id == currentPlanId) || null;
  const nextPlan = currentPlan ? findNextPlan(plans, currentPlan) : null;
  const included = featureLines(currentPlan, false);
  const extra = featureLines(nextPlan, true);

  const meters = [
    { key: SUBSCRIPTION_LIMITS.RESOURCES, label: 'Profissionais (agendas)', current: usage.resources },
    {
      key: SUBSCRIPTION_LIMITS.USERS,
      label: 'Usuários',
      current: usage.users,
      note: usage.pendingInvites > 0
        ? `Inclui ${usage.pendingInvites} ${usage.pendingInvites === 1 ? 'convite pendente' : 'convites pendentes'}`
        : null,
    },
    { key: SUBSCRIPTION_LIMITS.ROOMS, label: 'Salas', current: usage.rooms },
    { key: SUBSCRIPTION_LIMITS.PROCEDURES, label: 'Serviços e procedimentos', current: usage.procedures },
  ];

  if (!hasPlan) {
    return (
      <DetailsCard title="Uso do plano">
        <p className={styles.empty}>
          Sem plano ativo. O uso e os recursos voltam a aparecer aqui quando você escolher um plano.
        </p>
      </DetailsCard>
    );
  }

  return (
    <DetailsCard title="Uso do plano" subtitle="Quanto da sua conta já está em uso.">
      <div className={styles.meters}>
        {meters.map(({ key, ...meter }) => (
          <UsageMeter key={key} limit={getLimit(key)} {...meter} />
        ))}
      </div>

      {included.length > 0 && (
        <div className={styles.features}>
          <span className={styles.featuresTitle}>O que o {currentPlan.name} inclui</span>
          <ul className={styles.featureList}>
            {included.map((text) => (
              <li key={text} className={styles.feature}>
                <span className={`${styles.featureIcon} ${styles.featureOn}`}>
                  <CheckRoundedIcon fontSize="inherit" />
                </span>
                <span className={styles.featureLabel}>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {extra.length > 0 && (
        <div className={styles.features}>
          <span className={styles.featuresTitle}>Com o {nextPlan.name}, você também tem</span>
          <ul className={styles.featureList}>
            {extra.map((text) => (
              <li key={text} className={styles.feature}>
                <span className={`${styles.featureIcon} ${styles.featureOff}`}>
                  <LockOutlinedIcon fontSize="inherit" />
                </span>
                <span className={styles.featureLabelOff}>{text}</span>
              </li>
            ))}
          </ul>
          <Link to="/subscription/plans" className={styles.meterLink}>{LIMIT_UPGRADE_BUTTON}</Link>
        </div>
      )}

      {[...included, ...extra].some((text) => text.endsWith('*')) && (
        <p className={styles.featureHint}>* Integrações com serviços de terceiros, contratados e pagos à parte.</p>
      )}
    </DetailsCard>
  );
};

export default PlanUsageCard;
